import React, { useState } from 'react';
import { Droplets, AlertTriangle, CheckCircle, Eye, EyeOff, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DeleteAccount() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!identifier.trim() || !password) {
      setError('Completați toate câmpurile.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/delete-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Eroare la ștergerea contului.');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Droplets className="w-6 h-6 text-white" />
          </div>
          <span className="text-white font-bold text-xl">EcoWash</span>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl">
          {done ? (
            /* Success state */
            <div className="text-center space-y-4">
              <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-xl font-bold text-white">Cont șters</h1>
              <p className="text-slate-400 text-sm leading-relaxed">
                Contul tău și toate datele asociate (mașini, abonamente) au fost șterse definitiv din sistemul EcoWash.
              </p>
              <p className="text-slate-500 text-xs">
                Dacă ai întrebări, contactează-ne la <a href="mailto:contact@eco-wash.ro" className="text-primary underline">contact@eco-wash.ro</a>.
              </p>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="text-center mb-2">
                <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-6 h-6 text-red-400" />
                </div>
                <h1 className="text-xl font-bold text-white">Ștergere cont</h1>
                <p className="text-slate-400 text-sm mt-1 leading-relaxed">
                  Această acțiune este <strong className="text-white">ireversibilă</strong>. Contul, mașinile și abonamentele tale vor fi șterse definitiv.
                </p>
              </div>

              {/* Warning box */}
              <div className="flex gap-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <p className="text-red-300 text-xs leading-relaxed">
                  Datele șterse nu pot fi recuperate. Abonamentele active vor fi anulate fără rambursare.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="identifier" className="text-slate-300 text-sm mb-1.5 block">
                    Email sau telefon
                  </Label>
                  <Input
                    id="identifier"
                    data-testid="delete-identifier"
                    type="text"
                    placeholder="email@exemplu.com sau 07xxxxxxxx"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    autoComplete="username"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-slate-300 text-sm mb-1.5 block">
                    Parolă
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="delete-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Parola contului tău"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 pr-10"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {error && (
                <p data-testid="delete-error" className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <Button
                data-testid="delete-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {loading ? 'Se procesează...' : 'Șterge contul definitiv'}
              </Button>

              <p className="text-center text-slate-500 text-xs">
                Te-ai răzgândit?{' '}
                <a href="https://app.eco-wash.ro" className="text-primary hover:underline">
                  Înapoi la aplicație
                </a>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
